// Kontrolki formularza wg DS: Checkbox (F-8), Switch (F-7), NumberField ze stepperem (F-13),
// Collapse (M-2: miękkie pojawienie treści warunkowej).
import { useEffect, useRef, type ReactNode } from "react";

export function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  if (!open) return null;
  return <div className="reveal">{children}</div>;
}

export function Checkbox({
  checked,
  onChange,
  disabled,
  indeterminate,
  children,
  ariaLabel,
}: {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  indeterminate?: boolean;
  children?: ReactNode;
  ariaLabel?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return (
    <label className="choice">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <span className="choice__box choice__box--check" aria-hidden="true">
        <svg className="choice__mark" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M5 12l5 5L20 7" />
        </svg>
        <span className="choice__dash" />
      </span>
      {children != null ? <span>{children}</span> : null}
    </label>
  );
}

export function Switch({
  checked,
  onChange,
  disabled,
  ariaLabel,
  ariaLabelledBy,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}) {
  return (
    <button
      type="button"
      className="switch"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="switch__thumb" aria-hidden="true" />
    </button>
  );
}

export function NumberField({
  value,
  onChange,
  min,
  max,
  step = 1,
  placeholder,
  ariaLabel,
  id,
}: {
  value: number | null | "";
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
}) {
  const clamp = (n: number) => {
    let v = n;
    if (typeof min === "number") v = Math.max(min, v);
    if (typeof max === "number") v = Math.min(max, v);
    return v;
  };
  const stepBy = (dir: number) => {
    const base = typeof value === "number" ? value : (min ?? 0);
    onChange(clamp(base + dir * step));
  };
  return (
    <div className="stepper">
      <input
        id={id}
        className="field-input stepper__input"
        type="number"
        inputMode="numeric"
        value={value === null ? "" : value}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => {
          const raw = e.target.value.trim();
          onChange(raw === "" ? null : parseInt(raw, 10));
        }}
      />
      <div className="stepper__controls">
        <button type="button" className="stepper__btn stepper__btn--dec" aria-label="Zmniejsz wartość" onClick={() => stepBy(-1)}>
          −
        </button>
        <button type="button" className="stepper__btn stepper__btn--inc" aria-label="Zwiększ wartość" onClick={() => stepBy(1)}>
          +
        </button>
      </div>
    </div>
  );
}
